import { useState } from 'react';
import { Link2, Link2Off, Network } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Combobox } from '@/components/ui/Combobox';
import type { ComboboxOption } from '@/components/ui/Combobox';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import {
  useCustomerHnfLink,
  useHnfCustomers,
  useLinkHnf,
  useUnlinkHnf,
} from '@/hooks/useHnf';

/**
 * HNF-Mapping-Karte (WT-206, Sprint 2).
 *
 * - Status-Badge: verknüpft / nicht verknüpft
 * - Modal "HNF verknuepfen": Combobox aus /integrations/hnf/customers
 * - Wenn verknüpft: Read-only Anzeige der Kimai/Zammad-IDs aus identifier_map
 * - "Verknuepfung loesen" mit Confirm
 */

interface Props {
  customerId: string;
}

export function HnfIntegrationCard({ customerId }: Props): JSX.Element {
  const linkQuery = useCustomerHnfLink(customerId);
  const linkMut = useLinkHnf();
  const unlinkMut = useUnlinkHnf();

  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);

  const link = linkQuery.data;
  const isLinked = link != null;

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <Network className="size-4" aria-hidden="true" />
          HNF-Mapping
        </span>
      }
      description="Bridge zu Kimai / Zammad / weiteren Systemen via Identifier-Map."
      actions={
        isLinked ? (
          <Button
            variant="ghost"
            leftIcon={<Link2Off className="size-4" />}
            onClick={() => setUnlinkOpen(true)}
          >
            Lösen
          </Button>
        ) : (
          <Button leftIcon={<Link2 className="size-4" />} onClick={() => setLinkOpen(true)}>
            HNF verknüpfen
          </Button>
        )
      }
    >
      {linkQuery.isLoading && <p className="text-sm text-muted">Lädt ...</p>}
      {linkQuery.isError && <ErrorAlert error={linkQuery.error} />}
      {!linkQuery.isLoading && !isLinked && (
        <div className="flex items-center gap-2 text-sm">
          <Badge tone="neutral">Nicht verknüpft</Badge>
          <span className="text-muted">
            Ohne Mapping fehlen Kimai-Zeiterfassung und Zammad-Tickets-Bezüge.
          </span>
        </div>
      )}
      {link && (
        <div className="flex flex-col gap-2 text-sm">
          <div>
            <Badge tone="success">Mit HNF verknuepft (id {link.hnf_customer_id})</Badge>
            {link.hnf_name && (
              <span className="ml-2 text-muted">{link.hnf_name}</span>
            )}
          </div>
          <IdentifierMap map={link.identifier_map ?? {}} />
        </div>
      )}

      <LinkDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        customerId={customerId}
        onLink={async (hnfId) => {
          await linkMut.mutateAsync({ customerId, hnfCustomerId: hnfId });
          setLinkOpen(false);
        }}
        busy={linkMut.isPending}
        error={linkMut.error}
      />

      <Dialog
        open={unlinkOpen}
        onClose={() => setUnlinkOpen(false)}
        title="HNF-Verknüpfung lösen"
        description="Mapping wird entfernt. Re-Linking ist jederzeit möglich."
        footer={
          <>
            <Button variant="ghost" onClick={() => setUnlinkOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              loading={unlinkMut.isPending}
              onClick={async () => {
                await unlinkMut.mutateAsync(customerId);
                setUnlinkOpen(false);
              }}
            >
              Lösen
            </Button>
          </>
        }
      >
        {unlinkMut.isError && <ErrorAlert error={unlinkMut.error} />}
      </Dialog>
    </Card>
  );
}

function IdentifierMap({ map }: { map: Record<string, string> }): JSX.Element | null {
  const entries = Object.entries(map);
  if (entries.length === 0) return null;
  return (
    <dl className="grid grid-cols-3 gap-x-2 gap-y-1 rounded-md bg-surface-2 p-2 text-xs">
      {entries.map(([key, value]) => (
        <div key={key} className="contents">
          <dt className="text-muted">{key}</dt>
          <dd className="col-span-2 font-mono">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

interface LinkDialogProps {
  open: boolean;
  onClose: () => void;
  customerId: string;
  onLink: (hnfCustomerId: number) => Promise<void>;
  busy: boolean;
  error: unknown;
}

function LinkDialog({ open, onClose, onLink, busy, error }: LinkDialogProps): JSX.Element {
  const [search, setSearch] = useState<string>('');
  const [picked, setPicked] = useState<string | null>(null);
  const customersQuery = useHnfCustomers(search || undefined);

  const options: ComboboxOption[] = (customersQuery.data ?? []).map((c) => ({
    value: String(c.id),
    label: c.name,
    hint: [
      c.kimai_customer_id ? `Kimai ${c.kimai_customer_id}` : null,
      c.zammad_organization_id ? `Zammad ${c.zammad_organization_id}` : null,
    ]
      .filter(Boolean)
      .join(' · '),
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="HNF-Customer wählen"
      description="Kimai/Zammad-IDs aus identifier_map werden read-only übernommen."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            disabled={!picked}
            loading={busy}
            onClick={() => {
              if (!picked) return;
              const id = Number(picked);
              if (Number.isFinite(id)) void onLink(id);
            }}
          >
            Verknüpfen
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <input
          type="search"
          placeholder="Suche HNF-Kunde ..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-md border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label="HNF-Kunden filtern"
        />
        <Combobox
          label="HNF-Customer"
          value={picked}
          onChange={setPicked}
          options={options}
          placeholder="Customer wählen ..."
        />
        {customersQuery.isError && <ErrorAlert error={customersQuery.error} />}
        {error != null && <ErrorAlert error={error} />}
      </div>
    </Dialog>
  );
}
